import React, { useEffect, useState } from 'react';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import {
  fetchBrands,
  createBrand,
  updateBrand,
  updateBrandStatus,
  deleteBrand,
} from '../services/brandService';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit,
  Trash2,
} from 'lucide-react';

export default function ProductBrands() {
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ name: '', status: '1' });
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');

  const loadBrands = async () => {
    try {
      const res = await fetchBrands({ search, sortBy, sortOrder });
      const records = res.data?.data?.records || res.data?.data || [];
      setBrands(records);
    } catch (err) {
      toast.error('Failed to load brands');
    }
  };

  useEffect(() => {
    loadBrands();
  }, [sortBy, sortOrder]);

  useEffect(() => {
    if (search === '') loadBrands();
  }, [search]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, status: Number(form.status) };
    try {
      if (editingId) {
        await updateBrand(editingId, payload);
        toast.success('Brand updated');
      } else {
        await createBrand(payload);
        toast.success('Brand created');
      }
      closeForm();
      loadBrands();
    } catch (err) {
      toast.error('Failed to save brand');
    }
  };

  const openEdit = (brand) => {
    setEditingId(brand.id);
    setForm({ name: brand.name, status: String(brand.status) });
    setOpen(true);
  };

  const closeForm = () => {
    setEditingId(null);
    setForm({ name: '', status: '1' });
    setOpen(false);
  };

  const toggleStatus = async (brand) => {
    const newStatus = brand.status === 1 ? 2 : 1;
    const action = newStatus === 1 ? 'activate' : 'deactivate';
    const result = await Swal.fire({
      title: `Are you sure you want to ${action} this brand?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      buttonsStyling: false,
      customClass: {
        confirmButton:
          'bg-green-600 text-white px-4 py-2 rounded mr-2 hover:bg-green-700',
        cancelButton:
          'bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600',
      },
    });
    if (!result.isConfirmed) return;
    try {
      await updateBrandStatus(brand.id, newStatus);
      toast.success('Status updated');
      loadBrands();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (brand) => {
    const result = await Swal.fire({
      title: 'Are you sure you want to delete this brand?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'bg-red-600 text-white px-4 py-2 rounded mr-2 hover:bg-red-700',
        cancelButton: 'bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600',
      },
    });
    if (!result.isConfirmed) return;
    try {
      await deleteBrand(brand.id);
      toast.success('Brand deleted');
      loadBrands();
    } catch (err) {
      toast.error('Failed to delete brand');
    }
  };

  return (
    <LayoutWithSidebar>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-2 md:space-y-0">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            loadBrands();
          }}
          className="relative"
        >
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="border px-3 py-2 pl-8 pr-8 rounded w-full md:w-64 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                loadBrands();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
            >
              <X size={16} />
            </button>
          )}
        </form>

        <button
          onClick={() => setOpen(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded"
        >
          Add Brand
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white dark:bg-gray-800">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-700 text-left">
              <th
                className="py-2 px-3 cursor-pointer"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center space-x-1">
                  <span>Name</span>
                  {sortBy === 'name' ? (
                    sortOrder === 'ASC' ? (
                      <ArrowUp size={12} />
                    ) : (
                      <ArrowDown size={12} />
                    )
                  ) : (
                    <ArrowUpDown size={12} />
                  )}
                </div>
              </th>
              <th
                className="py-2 px-3 cursor-pointer"
                onClick={() => handleSort('createdAt')}
              >
                <div className="flex items-center space-x-1">
                  <span>Created At</span>
                  {sortBy === 'createdAt' ? (
                    sortOrder === 'ASC' ? (
                      <ArrowUp size={12} />
                    ) : (
                      <ArrowDown size={12} />
                    )
                  ) : (
                    <ArrowUpDown size={12} />
                  )}
                </div>
              </th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {brands.map((brand) => (
              <tr key={brand.id} className="border-t dark:border-gray-700">
                <td className="py-2 px-3">{brand.name}</td>
                <td className="py-2 px-3">{new Date(brand.createdAt).toLocaleDateString()}</td>
                <td className="py-2 px-3">
                  <button
                    className={`px-2 py-1 rounded text-white ${brand.status === 1 ? 'bg-green-500' : 'bg-red-500'}`}
                    onClick={() => toggleStatus(brand)}
                    title={`${brand.status === 1 ? 'Deactivate' : 'Activate'} brand`}
                  >
                    {brand.status === 1 ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="py-2 px-3 space-x-2">
                  <button
                    className="text-indigo-600"
                    onClick={() => openEdit(brand)}
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className="text-red-600"
                    onClick={() => handleDelete(brand)}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {brands.length === 0 && (
              <tr>
                <td className="py-2 px-3 text-center" colSpan="4">
                  <p className="text-sm text-gray-500 dark:text-gray-300">No brands</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-25 dark:bg-opacity-50 flex justify-end">
          <div className="relative bg-white dark:bg-gray-800 dark:text-white w-full sm:w-96 h-full p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl">{editingId ? 'Edit Brand' : 'Add Brand'}</h2>
              <button onClick={closeForm} className="text-gray-600 dark:text-gray-300">×</button>
            </div>
            <form onSubmit={handleSubmit} className="grid gap-4">
              <input
                name="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Name"
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <select
                name="status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="1">Active</option>
                <option value="2">Inactive</option>
              </select>
              <button type="submit" className="bg-indigo-600 text-white px-4 py-2 rounded">
                {editingId ? 'Update' : 'Create'} Brand
              </button>
            </form>
          </div>
        </div>
      )}
    </LayoutWithSidebar>
  );
}

